import { authFetch, fetchWithAuth, BASE_BACKEND_URL } from '../auth'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import Pagination from '../layout/Pagination'
import dayjs from '../utils/dayjsConfig'
import { DEFAULT_PER_PAGE } from '../constants/constant'
import useMediaQuery from '@mui/material/useMediaQuery'
import { alpha, useTheme } from '@mui/material/styles'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Grid'
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined'
import {
  TYPEDOCUMENTCHARGES,
  TYPESTATUSCHARGES,
  LABEL_TO_DOCUMENT_VALUE,
  LABEL_TO_STATUS_VALUE,
  FILTER_KEYS
} from './constants/chargeStatus'
import ChargesTable from './components/table/ChargeTable'
import ChargesFilters from './components/filters/ChargesFilters'
import { usePagination } from './helpers/usePagination'
import LoadingScreen from '../components/LoadingScreen'
import MobileEmptyStateCard from '../components/MobileEmptyStateCard'
import { layoutMobileMediaQuery } from '../layout/layoutConstants'
import { ChargeIndexMobileFilters, ChargeMobileCard, ChargeMobilePagination } from './mobile'
import {
  buildAppliedChipLabel,
  buildAppliedDateRange,
  buildSelectValue,
  countSelectedGroups,
  createEmptyDateRange,
  createEmptyDraftFilterSelections,
  dateRangeToPickerValue,
  dateRangeToQuery,
  formatDateRangeSummary,
  getSelectValues,
  normalizeDateRange,
  pickerValueToDateRange
} from './helpers/chargeMobileFilters'

const buildUniqueOptionRecords = (items, getValue, getLabel) => {
  const optionMap = new Map()

  items.forEach((item) => {
    const normalizedValue = String(getValue(item) || '').trim()

    if (normalizedValue && !optionMap.has(normalizedValue)) {
      optionMap.set(normalizedValue, {
        value: normalizedValue,
        label: getLabel(item)
      })
    }
  })

  return Array.from(optionMap.values())
}

const hasFilterPayload = (filtersPayload) => {
  if (!filtersPayload || Array.isArray(filtersPayload)) return false
  return Object.keys(filtersPayload).length > 0
}

function ChargeIndexView() {
  const theme = useTheme()
  const isMobileList = useMediaQuery(layoutMobileMediaQuery)
  const [isFetching, setIsFetching] = useState(true)
  const [query, setQuery] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [totalPages, setTotalPages] = useState(0)
  const [title] = useState('Cobros')
  const [data, setData] = useState([])
  const [filtersfetch, setFilterFetch] = useState({})
  const [filters, setFilters] = useState([])
  const [mobileFilterConfigs, setMobileFilterConfigs] = useState([])
  const [resetReactFilters, setResetReactFilters] = useState(false)
  const [labelToIdMapping, setLabelToIdMapping] = useState({})
  const [idToLabelMapping, setIdToLabelMapping] = useState(new Map())
  const [allSelectedOptions, setAllSelectedOptions] = useState(new Map())
  const [filterQuery, setFilterQuery] = useState('')
  const [dateQuery, setDateQuery] = useState(null)
  const [selectedDates, setSelectedDates] = useState(null)
  const [perPage] = useState(DEFAULT_PER_PAGE)
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false)
  const [draftFilterSelections, setDraftFilterSelections] = useState(
    createEmptyDraftFilterSelections()
  )
  const [draftDateRange, setDraftDateRange] = useState(createEmptyDateRange())
  const [expandedChargeKey, setExpandedChargeKey] = useState(null)
  const filtersDisabled = !hasFilterPayload(filtersfetch)
  const { currentPage, setCurrentPage, handlePaginationClick } = usePagination(1)

  ////// Stops fetchs when page is changed
  const abortControllerRef = useRef(new AbortController())

  useEffect(() => {
    const abortCurrentFetchs = () => {
      abortControllerRef.current.abort('Fetch Aborted by User.')
    }
    window.addEventListener('beforeunload', abortCurrentFetchs)

    // Cleanup function
    return () => {
      window.removeEventListener('beforeunload', abortCurrentFetchs)
    }
  }, [])
  //////

  const fetchChargesFilters = async () => {
    try {
      const response = await fetchWithAuth(`${BASE_BACKEND_URL}/charges?filters_names_only=true`, {
        method: 'GET',
        signal: abortControllerRef.current.signal
      })

      if (response === undefined) {
        setFilterFetch({})
        console.error('No response from the server.')
        setIsFetching(false)
        return
      }
      setFilterFetch(response.result)
    } catch (error) {
      console.error('Error fetching clients:', error)
      setFilterFetch({})
      setTotalPages(0)
    } finally {
      setIsFetching(false)
    }
  }

  const fetchChargesFromServer = async () => {
    try {
      const response = await authFetch(
        `${BASE_BACKEND_URL}/charges?page=${currentPage}&per_page=${perPage}${filterQuery || dateQuery ? '&filter_search=true' : ''}${filterQuery || ''}${dateQuery || ''}${query ? `&q=${query}` : ''}`,
        {
          method: 'GET',
          signal: abortControllerRef.current.signal
        }
      )
      if (!response || response.status != 200) {
        setData([])
        setTotalPages(0)
      } else {
        let data = await response.json()
        setData(data.table ? data.table : data)
        setTotalPages(Math.ceil((data.count || data.length) / perPage))
      }
    } catch (error) {
      if (error !== 'Fetch Aborted by User.') {
        console.error('Error:', error)
      }
    }
  }

  const openChargeInNewTab = (uuid) => {
    window.open('/cobros/' + uuid, '_blank')
  }

  const processChargesForFilter = () => {
    let labelToIdMappingTemp = {}
    const idToLabelMappingTemp = new Map()

    const userMobileOptions = buildUniqueOptionRecords(
      filtersfetch.user_charges || [],
      (user) => user.user_uid,
      (user) => `${user.user_uid} - ${user.user_full_name}`
    )

    userMobileOptions.forEach((option) => {
      labelToIdMappingTemp[option.label] = option.value
      idToLabelMappingTemp.set(option.value, option.label)
    })

    const userOptions = userMobileOptions.map((option) => option.label)

    const clientMobileOptions = buildUniqueOptionRecords(
      filtersfetch.clients_charges || [],
      (client) => client.client_uid,
      (client) => `${client.client_uid} - ${client.client_name}`
    )

    clientMobileOptions.forEach((option) => {
      labelToIdMappingTemp[option.label] = option.value
      idToLabelMappingTemp.set(option.value, option.label)
    })

    const clientOptions = clientMobileOptions.map((option) => option.label)

    const documentTypeMobileOptions = TYPEDOCUMENTCHARGES.map((label) => {
      const value = LABEL_TO_DOCUMENT_VALUE[label] || label
      labelToIdMappingTemp[label] = value
      idToLabelMappingTemp.set(value, label)
      return { label, value }
    })

    const statusMobileOptions = TYPESTATUSCHARGES.map((label) => {
      const value = LABEL_TO_STATUS_VALUE[label] || label
      labelToIdMappingTemp[label] = value
      idToLabelMappingTemp.set(value, label)
      return { label, value }
    })

    TYPEDOCUMENTCHARGES.forEach((label) => {
      labelToIdMappingTemp[label] = LABEL_TO_DOCUMENT_VALUE[label]
    })

    TYPESTATUSCHARGES.forEach((label) => {
      labelToIdMappingTemp[label] = LABEL_TO_STATUS_VALUE[label]
    })

    setFilters([
      { key: FILTER_KEYS.USER, value: userOptions },
      { key: FILTER_KEYS.CLIENT, value: clientOptions },
      { key: FILTER_KEYS.DOCUMENT_TYPE, value: TYPEDOCUMENTCHARGES },
      { key: FILTER_KEYS.STATUS, value: TYPESTATUSCHARGES }
    ])
    setMobileFilterConfigs([
      {
        key: FILTER_KEYS.USER,
        title: FILTER_KEYS.USER,
        options: userMobileOptions
      },
      {
        key: FILTER_KEYS.CLIENT,
        title: FILTER_KEYS.CLIENT,
        options: clientMobileOptions
      },
      {
        key: FILTER_KEYS.DOCUMENT_TYPE,
        title: FILTER_KEYS.DOCUMENT_TYPE,
        options: documentTypeMobileOptions
      },
      {
        key: FILTER_KEYS.STATUS,
        title: FILTER_KEYS.STATUS,
        options: statusMobileOptions
      }
    ])

    setLabelToIdMapping(labelToIdMappingTemp)
    setIdToLabelMapping(idToLabelMappingTemp)
  }

  const buildFiltersQuery = (selectedOptions = allSelectedOptions) => {
    let queryParts = []

    const userUids = selectedOptions?.get(FILTER_KEYS.USER) || []
    if (userUids.length > 0) {
      queryParts.push(`user_uid=${encodeURIComponent(userUids.join(','))}`)
    }

    const clientUids = selectedOptions?.get(FILTER_KEYS.CLIENT) || []
    if (clientUids.length > 0) {
      queryParts.push(`client_uid=${encodeURIComponent(clientUids.join(','))}`)
    }

    const document_type_labels = selectedOptions?.get(FILTER_KEYS.DOCUMENT_TYPE) || []
    if (document_type_labels.length > 0) {
      const values = document_type_labels.map((label) => LABEL_TO_DOCUMENT_VALUE[label] || label)
      queryParts.push(`document_type=${encodeURIComponent(values.join(','))}`)
    }

    const status_labels = selectedOptions?.get(FILTER_KEYS.STATUS) || []
    if (status_labels.length > 0) {
      const values = status_labels.map((label) => LABEL_TO_STATUS_VALUE[label] || label)
      queryParts.push(`status=${encodeURIComponent(values.join(','))}`)
    }

    const queryParam = queryParts.length > 0 ? `&${queryParts.join('&')}` : ''
    setFilterQuery(queryParam)
    setCurrentPage(1)
  }

  const resetFilters = () => {
    setAllSelectedOptions(new Map())
    setFilterQuery('')
    setDateQuery(null)
    setSelectedDates(null)
    setSearchInput('')
    setQuery('')
    setCurrentPage(1)
    setDraftFilterSelections(createEmptyDraftFilterSelections())
    setDraftDateRange(createEmptyDateRange())
    setResetReactFilters(!resetReactFilters)
  }

  const applySearchQuery = (nextQuery) => {
    setSearchInput(nextQuery)
    setQuery(nextQuery)
    setCurrentPage(1)
  }

  const clearSearchInput = () => {
    applySearchQuery('')
  }

  const handleMobileSearchChange = (event) => {
    setSearchInput(event.target.value)
  }

  const handleMobileSearchKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      applySearchQuery(searchInput)
    }
  }

  const handleDateFilter = (datesObj) => {
    let queryParam = ''
    let startDate, endDate

    if (!datesObj || datesObj.length === 0) {
      setDateQuery(null)
      setSelectedDates(null)
      setCurrentPage(1)
    } else {
      setSelectedDates(datesObj)

      let formattedDates = datesObj.map((date) => dayjs.unix(date.unix))

      if (formattedDates.length > 1) {
        startDate = dayjs.min(formattedDates)
        endDate = dayjs.max(formattedDates)
      } else {
        startDate = formattedDates[0]
        endDate = formattedDates[0]
      }

      const startDateISO = startDate.toISOString()
      const endDateISO = endDate.toISOString()
      queryParam = `&start_date=${startDateISO}&end_date=${endDateISO}`

      setDateQuery(queryParam)
      setCurrentPage(1)
    }
  }

  useEffect(() => {
    if (filtersfetch) {
      processChargesForFilter()
    }
  }, [filtersfetch])

  useEffect(() => {
    if (allSelectedOptions) {
      buildFiltersQuery()
    }
  }, [allSelectedOptions])

  useEffect(() => {
    fetchChargesFromServer()
  }, [query, filterQuery, dateQuery, currentPage])

  useEffect(() => {
    fetchChargesFilters()
  }, [])

  const buildDraftSelectionsFromApplied = useCallback(
    () => ({
      [FILTER_KEYS.USER]: buildSelectValue(allSelectedOptions.get(FILTER_KEYS.USER)),
      [FILTER_KEYS.CLIENT]: buildSelectValue(allSelectedOptions.get(FILTER_KEYS.CLIENT)),
      [FILTER_KEYS.DOCUMENT_TYPE]: buildSelectValue(
        allSelectedOptions.get(FILTER_KEYS.DOCUMENT_TYPE)
      ),
      [FILTER_KEYS.STATUS]: buildSelectValue(allSelectedOptions.get(FILTER_KEYS.STATUS))
    }),
    [allSelectedOptions]
  )

  const syncMobileDraftWithApplied = useCallback(() => {
    setDraftFilterSelections(buildDraftSelectionsFromApplied())
    setDraftDateRange(pickerValueToDateRange(selectedDates))
  }, [buildDraftSelectionsFromApplied, selectedDates])

  const handleOpenMobileFilters = () => {
    syncMobileDraftWithApplied()
    setIsMobileFiltersOpen(true)
  }

  const handleCloseMobileFilters = () => {
    syncMobileDraftWithApplied()
    setIsMobileFiltersOpen(false)
  }

  const handleDraftSelectChange = (filterKey, nextValue) => {
    setDraftFilterSelections((prevValue) => ({
      ...prevValue,
      [filterKey]: buildSelectValue(getSelectValues(nextValue))
    }))
  }

  const handleDraftDateChange = (boundary, nextValue) => {
    const normalizedValue = nextValue?.isValid?.() ? nextValue.startOf('day') : null

    setDraftDateRange((prevRange) =>
      normalizeDateRange({
        ...prevRange,
        [boundary]: normalizedValue
      })
    )
  }

  const clearDraftDateRange = () => {
    setDraftDateRange(createEmptyDateRange())
  }

  const clearAllMobileFilters = () => {
    resetFilters()
    setIsMobileFiltersOpen(false)
  }

  const applyMobileFilters = () => {
    const nextSelectedFilterOptions = new Map()
    const nextDateRange = buildAppliedDateRange(draftDateRange)

    Object.values(FILTER_KEYS).forEach((filterKey) => {
      nextSelectedFilterOptions.set(filterKey, [...getSelectValues(draftFilterSelections[filterKey])])
    })

    setAllSelectedOptions(nextSelectedFilterOptions)
    setSelectedDates(dateRangeToPickerValue(nextDateRange))
    setDateQuery(dateRangeToQuery(nextDateRange))
    setCurrentPage(1)
    setIsMobileFiltersOpen(false)
  }

  const selectedDateRange = useMemo(() => pickerValueToDateRange(selectedDates), [selectedDates])

  const appliedFilterSelections = useMemo(
    () => ({
      [FILTER_KEYS.USER]: buildSelectValue(allSelectedOptions.get(FILTER_KEYS.USER)),
      [FILTER_KEYS.CLIENT]: buildSelectValue(allSelectedOptions.get(FILTER_KEYS.CLIENT)),
      [FILTER_KEYS.DOCUMENT_TYPE]: buildSelectValue(
        allSelectedOptions.get(FILTER_KEYS.DOCUMENT_TYPE)
      ),
      [FILTER_KEYS.STATUS]: buildSelectValue(allSelectedOptions.get(FILTER_KEYS.STATUS))
    }),
    [allSelectedOptions]
  )

  const appliedFilterGroupCount = useMemo(
    () => countSelectedGroups(appliedFilterSelections, selectedDateRange),
    [appliedFilterSelections, selectedDateRange]
  )

  const appliedFilterChips = useMemo(() => {
    const chips = []
    const userValues = getSelectValues(appliedFilterSelections[FILTER_KEYS.USER])
    const clientValues = getSelectValues(appliedFilterSelections[FILTER_KEYS.CLIENT])
    const documentTypeValues = getSelectValues(appliedFilterSelections[FILTER_KEYS.DOCUMENT_TYPE])
    const statusValues = getSelectValues(appliedFilterSelections[FILTER_KEYS.STATUS])
    const appliedDateRange = buildAppliedDateRange(selectedDateRange)

    if (userValues.length > 0) {
      chips.push({
        key: FILTER_KEYS.USER,
        label: buildAppliedChipLabel({
          singularLabel: 'Usuario',
          pluralLabel: 'Usuarios',
          values: userValues,
          idToLabelMapping
        })
      })
    }

    if (clientValues.length > 0) {
      chips.push({
        key: FILTER_KEYS.CLIENT,
        label: buildAppliedChipLabel({
          singularLabel: 'Cliente',
          pluralLabel: 'Clientes',
          values: clientValues,
          idToLabelMapping
        })
      })
    }

    if (documentTypeValues.length > 0) {
      chips.push({
        key: FILTER_KEYS.DOCUMENT_TYPE,
        label: buildAppliedChipLabel({
          singularLabel: 'Tipo',
          pluralLabel: 'Tipos',
          values: documentTypeValues,
          idToLabelMapping
        })
      })
    }

    if (statusValues.length > 0) {
      chips.push({
        key: FILTER_KEYS.STATUS,
        label: buildAppliedChipLabel({
          singularLabel: 'Estado',
          pluralLabel: 'Estados',
          values: statusValues,
          idToLabelMapping
        })
      })
    }

    if (appliedDateRange.start) {
      chips.push({
        key: 'date-range',
        label: `Fechas: ${formatDateRangeSummary(appliedDateRange, '')}`
      })
    }

    return chips
  }, [appliedFilterSelections, idToLabelMapping, selectedDateRange])

  const handleMobilePagination = (targetId) => {
    const numericPage = Number.parseInt(targetId, 10)

    switch (targetId) {
      case 'first-page':
        setCurrentPage(1)
        break
      case 'last-page':
        setCurrentPage(totalPages)
        break
      case 'previous-page':
        setCurrentPage((prevPage) => Math.max(prevPage - 1, 1))
        break
      case 'next-page':
        setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages))
        break
      default:
        if (Number.isInteger(numericPage) && numericPage >= 1 && numericPage <= totalPages) {
          setCurrentPage(numericPage)
        }
        break
    }
  }

  const toggleChargeCardExpand = (cardKey) => {
    setExpandedChargeKey((prev) => (prev === cardKey ? null : cardKey))
  }

  const renderMobileCards = () => {
    if (!data || data.length === 0) {
      return (
        <Box sx={{ mt: 1 }}>
          <MobileEmptyStateCard
            icon={<ArticleOutlinedIcon fontSize="small" />}
            title="No hay cobros disponibles"
            description="Cuando existan cobros para los filtros seleccionados, aparecerán aquí con su detalle."
          />
        </Box>
      )
    }

    return (
      <Box
        sx={() => ({
          mt: 1,
          mb: 0.5,
          py: 2,
          px: { xs: 0.5, sm: 1 },
          mx: { xs: -0.5, sm: -1 },
          borderRadius: 2,
          bgcolor: alpha(theme.palette.text.primary, theme.palette.mode === 'dark' ? 0.06 : 0.04)
        })}
      >
        <Grid container spacing={2.5}>
          {data.map((model, index) => {
            const cardKey = `${model.header_db_ref || model.document_reference || 'charge'}-${index}`

            return (
              <ChargeMobileCard
                key={cardKey}
                model={model}
                openChargeInNewTab={openChargeInNewTab}
                expanded={expandedChargeKey === cardKey}
                onToggleExpand={() => toggleChargeCardExpand(cardKey)}
              />
            )
          })}
        </Grid>
      </Box>
    )
  }

  return (
    <div>
      <div>
        <div>
          {isFetching && <LoadingScreen text="Cargando..." />}
          {!isFetching && (
            <>
              <div className="index-header detailed-container">
                <h2>{title}</h2>
              </div>
              <div className="index-table">
                <div className="show-area">
                  {isMobileList ? (
                    <ChargeIndexMobileFilters
                      query={searchInput}
                      handleSearchChange={handleMobileSearchChange}
                      handleSearchKeyDown={handleMobileSearchKeyDown}
                      clearSearch={clearSearchInput}
                      openFilters={handleOpenMobileFilters}
                      appliedFilterGroupCount={appliedFilterGroupCount}
                      appliedFilterChips={appliedFilterChips}
                      isOpen={isMobileFiltersOpen}
                      closeFilters={handleCloseMobileFilters}
                      mobileFilterConfigs={mobileFilterConfigs}
                      draftFilterSelections={draftFilterSelections}
                      handleDraftSelectChange={handleDraftSelectChange}
                      anyFilterEmpty={filtersDisabled}
                      draftDateRange={draftDateRange}
                      handleDraftDateChange={handleDraftDateChange}
                      clearDraftDateRange={clearDraftDateRange}
                      clearAllFilters={clearAllMobileFilters}
                      applyFilters={applyMobileFilters}
                    />
                  ) : (
                    <ChargesFilters
                      filters={filters}
                      resetReactFilters={resetReactFilters}
                      filtersDisabled={filtersDisabled}
                      allSelectedOptions={allSelectedOptions}
                      setAllSelectedOptions={setAllSelectedOptions}
                      labelToIdMapping={labelToIdMapping}
                      selectedDates={selectedDates}
                      handleDateFilter={handleDateFilter}
                      query={searchInput}
                      setQuery={applySearchQuery}
                      fetchChargesFromServer={() => {}}
                      searchQuery={query}
                      filterQuery={filterQuery}
                      dateQuery={dateQuery}
                      resetFilters={resetFilters}
                    />
                  )}
                  {isMobileList ? renderMobileCards() : <ChargesTable data={data} showModel={openChargeInNewTab} />}
                  {isMobileList && totalPages > 1 ? (
                    <Box sx={{ mt: 1 }}>
                      <ChargeMobilePagination
                        pagination={{
                          current_page: currentPage,
                          total_pages: totalPages,
                          per_page: perPage
                        }}
                        handlePagination={handleMobilePagination}
                      />
                    </Box>
                  ) : null}
                </div>
              </div>

              {!isMobileList && totalPages > 1 && (
                <nav className="index-table">
                  <Pagination
                    current_page={currentPage}
                    total_pages={totalPages}
                    handle_pagination={(e) => handlePaginationClick(e, totalPages)}
                  />
                </nav>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
export default ChargeIndexView
